import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import { useToast } from "../../components/Toast/toastHook";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import ListingThumbnail from "../../components/listings/ListingThumbnail";
import {
  GET_AGENT_FAVORITES,
  REMOVE_FROM_FAVORITES,
} from "../../graphql/favoriteQueries";
import fullDate from "../../utils/fullDate";
import priceFormat from "../../utils/priceFormat";
import { Table } from "antd";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import { favoriteSorters } from "../../utils/agentTableSorters";
import { TABLE_SCROLL } from "../../constants/table";

const AgentFavorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirmDialog();
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const toast = useToast(4000);

  const { loading, error, data, refetch } = useQuery(GET_AGENT_FAVORITES, {
    variables: { agentId: currentUser?.id },
    fetchPolicy: "network-only",
    skip: !currentUser?.id,
  });

  useEffect(() => {
    if (data?.getAgentFavorites) {
      setFavorites(data.getAgentFavorites);
    }
  }, [data]);

  useEffect(() => {
    if (!error) return;
    toast("error", error.message);
  }, [error]);

  const [removeFavorite] = useMutation(REMOVE_FROM_FAVORITES, {
    onCompleted(result) {
      toast("success", "Removed from favorites");
      setFavorites((prev) =>
        prev.filter((f) => f._id !== result.removeFromFavorites._id)
      );
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const handleRemove = (favorite) =>
    confirm({
      title: `Remove ${favorite.listing.streetAddr} from favorites?`,
      description: "You can favorite this listing again later.",
      confirmLabel: "Remove",
      onConfirm: () =>
        removeFavorite({ variables: { favoriteId: favorite._id } }),
    });

  const handleSearch = (e) => {
    setSearchText(e.target.value.toLowerCase());
  };

  // Filter favorites based on search
  const filteredFavorites = favorites.filter((favorite) => {
    if (!searchText) return true;
    const listing = favorite.listing;
    const searchableText =
      `${listing.streetAddr} ${listing.city} ${listing.state} ${listing.zip}`.toLowerCase();
    return searchableText.includes(searchText);
  });

  const columns = [
    {
      title: "Photo",
      key: "photo",
      skeleton: "thumb",
      width: 112,
      render: (_, record) => (
        <ListingThumbnail photos={record.listing?.imageURLs ?? []} size={77} preview />
      ),
    },
    {
      title: "Address",
      dataIndex: "listing",
      key: "address",
      sorter: favoriteSorters.address,
      skeleton: "twoLine",
      render: (listing) => (
        <div className="text-black flex-col">
          <div>{`${listing.streetAddr},`}</div>
          <div>{`${listing.city}, ${listing.state}, ${listing.zip}`}</div>
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "listing",
      key: "price",
      sorter: favoriteSorters.price,
      render: (listing) => (
        <span className="font-medium text-black">
          ${priceFormat(listing.price)}
        </span>
      ),
    },
    {
      title: "Listed By",
      dataIndex: "listing",
      key: "agent",
      sorter: favoriteSorters.agent,
      render: (listing) => (
        <span className="font-medium text-black">{`${listing.user?.firstName} ${listing.user?.lastName}`}</span>
      ),
    },
    {
      title: "Live Date",
      dataIndex: "listing",
      key: "expiration",
      sorter: favoriteSorters.expiration,
      render: (listing) => (
        <span className="font-medium text-black">
          {fullDate(listing.expiration)}
        </span>
      ),
    },
    {
      title: "Favorited On",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: favoriteSorters.createdAt,
      render: (createdAt) => (
        <span className="font-medium text-black">{fullDate(createdAt)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-3.5">
          <button
            className="hover:text-primary"
            onClick={() =>
              navigate(
                `/company/${companyId}/agent/listings/${record.listing._id}/view`
              )
            }
            title="View Listing"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
              <path
                fillRule="evenodd"
                d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            className="hover:text-red-500"
            onClick={() => handleRemove(record)}
            title="Remove from Favorites"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // Page chrome stays mounted while loading; only the table body skeletons.
  const firstLoad = loading && !favorites.length;

  return (
    <div id="agent-favorites">
      <Breadcrumb pageName="My Favorites" />
      <div className="pb-6">
        <Card>
          <div className="m-4">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 sm:w-auto">
                <span className="whitespace-nowrap font-bold text-lg">
                  My Favorites ({filteredFavorites.length})
                </span>
                <div className="relative w-full sm:w-72">
                  <div className="absolute top-1/2 left-1 -translate-y-1/2">
                    <svg
                      className="fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z"
                        fill=""
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z"
                        fill=""
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    onChange={handleSearch}
                    placeholder="Search by address..."
                    className="w-full rounded-md border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none"
                  />
                </div>
              </div>
            </div>
          </div>
          {error && !favorites.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <div key={firstLoad ? "skeleton" : "data"} className="animate-riseIn">
              <Table
                scroll={TABLE_SCROLL}
                bordered
                rowKey="_id"
                pagination={{
                  position: ["bottomCenter"],
                  pageSize: 10,
                  showSizeChanger: true,
                }}
                {...(firstLoad
                  ? skeletonTableProps(columns, 10)
                  : { columns, dataSource: filteredFavorites })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No favorites match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No favorites yet"
                      description="Listings you favorite from the Whiteboard will appear here."
                    />
                  ),
                }}
              />
              </div>
            </>
          )}
        </Card>
      </div>

      {dialog}
    </div>
  );
};

export default AgentFavorites;
